import React, { useEffect, useState } from "react";
import { api, ApiError, errorMessage, fileUrl } from "../api/client";
import type { Part } from "../api/types";
import { Button, Field, Input } from "../components/Controls";
import { Modal } from "../components/Modal";
import { Dropzone } from "../components/Dropzone";
import { toast } from "../components/Toast";

const MAX_POINTS = 10;

export function PartFormModal({ open, part, onClose, onSaved }: { open: boolean; part?: Part | null; onClose: () => void; onSaved: (p: Part) => void }) {
  const [number, setNumber] = useState("");
  const [name, setName] = useState("");
  const [points, setPoints] = useState(["Punto 1", "Punto 2", "Punto 3", "Punto 4"]);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNumber(part?.part_number || "");
    setName(part?.name || "");
    setPoints(part?.points?.length ? [...part.points] : ["Punto 1", "Punto 2", "Punto 3", "Punto 4"]);
    setFile(null);
    setPreview(fileUrl(part?.reference_image_url));
    setErrors({});
  }, [open, part]);

  const pick = (files: File[]) => {
    setFile(files[0]);
    setPreview(URL.createObjectURL(files[0]));
  };

  const save = async () => {
    const errs: Record<string, string> = {};
    if (!number.trim()) errs.part_number = "El número de parte es obligatorio.";
    if (!name.trim()) errs.name = "El nombre es obligatorio.";
    points.forEach((p, i) => {
      if (!p.trim()) errs[`p${i}`] = "Nombre obligatorio.";
    });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const fd = new FormData();
    fd.append("part_number", number.trim());
    fd.append("name", name.trim());
    fd.append("points", JSON.stringify(points.map((p) => p.trim())));
    if (file) fd.append("reference_image", file);
    setBusy(true);
    try {
      const saved = part ? await api.form<Part>("PUT", `/parts/${part.id}`, fd) : await api.form<Part>("POST", "/parts", fd);
      toast.success(part ? "Pieza guardada" : "Pieza creada");
      onSaved(saved);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setErrors({ part_number: e.message });
      else if (e instanceof ApiError && /punto/i.test(e.message)) setErrors({ p0: e.message });
      else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={part ? "Editar pieza" : "Nueva pieza"}
      testId="part-form-modal"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} loading={busy} data-testid="part-form-save-button">Guardar</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Número de parte" error={errors.part_number}>
            <Input value={number} onChange={(e) => setNumber(e.target.value)} className="font-mono" data-testid="part-form-number-input" />
          </Field>
          <Field label="Nombre" error={errors.name}>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="part-form-name-input" />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {points.map((p, i) => (
            <Field key={i} label={`Punto ${i + 1}`} error={errors[`p${i}`]}>
              <div className="flex gap-2">
                <Input value={p} onChange={(e) => setPoints(points.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`part-form-point-${i}-input`} />
                {points.length > 1 && (!part || i === points.length - 1) && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setPoints(points.filter((_, j) => j !== i))} aria-label={`Quitar punto ${i + 1}`} data-testid={`part-form-point-${i}-remove-button`}>
                    Quitar
                  </Button>
                )}
              </div>
            </Field>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500" data-testid="part-form-points-count">
            {points.length} {points.length === 1 ? "punto" : "puntos"} de inspección · máximo {MAX_POINTS}
          </span>
          <Button type="button" variant="secondary" size="sm" disabled={points.length >= MAX_POINTS} onClick={() => setPoints([...points, `Punto ${points.length + 1}`])} data-testid="part-form-add-point-button">
            Agregar punto
          </Button>
        </div>
        <Field label="Imagen de referencia">
          <Dropzone onFiles={pick} label="Arrastrar la foto de referencia" testId="part-form-reference-dropzone">
            {preview ? <img src={preview} alt="Referencia" className="mx-auto max-h-40 rounded object-contain" data-testid="part-form-reference-preview" /> : undefined}
          </Dropzone>
        </Field>
      </div>
    </Modal>
  );
}
